import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, DollarSign, Shield, Clock, AlertTriangle } from 'lucide-react';
import PageSeo from '../components/PageSeo';

const GiftCardGuide = () => {
  useEffect(() => {
    // Analytics only (SEO metadata handled by PageSeo)
    if (typeof window.gtag !== 'undefined') {
      window.gtag('event', 'page_view', {
        'page_title': 'Ultimate Guide to Selling Gift Cards',
        'page_location': window.location.href,
        'event_category': 'seo_content'
      });
    }
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-purple-50">
      <PageSeo
        title="How to Sell Gift Cards — Complete Guide | CashifyGCMart"
        description="Complete guide on how to sell gift cards for cash safely. Learn about rates, security, and best practices. Get up to 93% of card value with same-day payment."
        path="/guide/how-to-sell-gift-cards"
      />
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Complete Guide to Selling Gift Cards for Cash in 2024
          </h1>
          <p className="text-xl md:text-2xl opacity-90 mb-8">
            Learn how to turn unused gift cards into instant cash safely and securely
          </p>
          <div className="flex flex-wrap justify-center gap-4 text-sm">
            <span className="bg-white/20 px-4 py-2 rounded-full">✓ Up to 93% Value</span>
            <span className="bg-white/20 px-4 py-2 rounded-full">✓ Same-Day Payment</span>
            <span className="bg-white/20 px-4 py-2 rounded-full">✓ 100% Secure</span>
          </div>
        </div>
      </div>

      {/* Table of Contents */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-lg mb-12">
          <h2 className="text-2xl font-bold mb-4 text-gray-800">Table of Contents</h2>
          <div className="grid md:grid-cols-2 gap-2">
            <a href="#why-sell" className="text-blue-600 hover:underline">1. Why Sell Gift Cards?</a>
            <a href="#how-it-works" className="text-blue-600 hover:underline">2. How Gift Card Selling Works</a>
            <a href="#choosing-platform" className="text-blue-600 hover:underline">3. Choosing the Right Platform</a>
            <a href="#safety-tips" className="text-blue-600 hover:underline">4. Safety and Security Tips</a>
            <a href="#maximizing-value" className="text-blue-600 hover:underline">5. Maximizing Your Card Value</a>
            <a href="#step-by-step" className="text-blue-600 hover:underline">6. Step-by-Step Selling Process</a>
          </div>
        </div>

        {/* Content Sections */}
        <div className="prose prose-lg max-w-none">
          {/* Section 1 */}
          <section id="why-sell" className="mb-12">
            <h2 className="text-3xl font-bold mb-6 text-gray-800">Why Sell Gift Cards for Cash?</h2>
            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <DollarSign className="text-green-500 mt-1 flex-shrink-0" size={24} />
                  <div>
                    <h3 className="font-semibold text-lg">Immediate Cash Access</h3>
                    <p className="text-gray-600">Get cash within 24 hours instead of waiting to use the gift card</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="text-green-500 mt-1 flex-shrink-0" size={24} />
                  <div>
                    <h3 className="font-semibold text-lg">No Expiration Worries</h3>
                    <p className="text-gray-600">Convert cards before they expire or lose value</p>
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <Shield className="text-green-500 mt-1 flex-shrink-0" size={24} />
                  <div>
                    <h3 className="font-semibold text-lg">Unwanted Cards</h3>
                    <p className="text-gray-600">Turn unwanted gifts into money you can actually use</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Clock className="text-green-500 mt-1 flex-shrink-0" size={24} />
                  <div>
                    <h3 className="font-semibold text-lg">Emergency Funds</h3>
                    <p className="text-gray-600">Quick access to cash for unexpected expenses</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Section 2 */}
          <section id="how-it-works" className="mb-12">
            <h2 className="text-3xl font-bold mb-6 text-gray-800">How Gift Card Selling Works</h2>
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-8 rounded-lg mb-8">
              <div className="grid md:grid-cols-4 gap-6 text-center">
                <div className="space-y-3">
                  <div className="bg-blue-500 text-white rounded-full w-12 h-12 flex items-center justify-center mx-auto text-xl font-bold">1</div>
                  <h3 className="font-semibold">Get Quote</h3>
                  <p className="text-sm text-gray-600">Enter card details for instant rate</p>
                </div>
                <div className="space-y-3">
                  <div className="bg-purple-500 text-white rounded-full w-12 h-12 flex items-center justify-center mx-auto text-xl font-bold">2</div>
                  <h3 className="font-semibold">Submit Card</h3>
                  <p className="text-sm text-gray-600">Upload card images or codes</p>
                </div>
                <div className="space-y-3">
                  <div className="bg-pink-500 text-white rounded-full w-12 h-12 flex items-center justify-center mx-auto text-xl font-bold">3</div>
                  <h3 className="font-semibold">Verification</h3>
                  <p className="text-sm text-gray-600">We verify card authenticity</p>
                </div>
                <div className="space-y-3">
                  <div className="bg-green-500 text-white rounded-full w-12 h-12 flex items-center justify-center mx-auto text-xl font-bold">4</div>
                  <h3 className="font-semibold">Get Paid</h3>
                  <p className="text-sm text-gray-600">Receive cash same day</p>
                </div>
              </div>
            </div>
            
            <h3 className="text-2xl font-bold mb-4">Gift Card Market Overview</h3>
            <p className="text-gray-700 mb-4">
              The gift card resale market has grown significantly, with Americans holding over $21 billion in unused gift cards. 
              Professional platforms like Cashifygcmart provide secure, regulated environments for converting these cards to cash.
            </p>
            
            <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6 rounded-r-lg mb-6">
              <h4 className="font-bold text-lg mb-2">💡 Did You Know?</h4>
              <p>Over 47% of Americans have at least one unused gift card, with an average value of $175 per household.</p>
            </div>
          </section>

          {/* Section 3 */}
          <section id="choosing-platform" className="mb-12">
            <h2 className="text-3xl font-bold mb-6 text-gray-800">Choosing the Right Gift Card Exchange Platform</h2>
            
            <h3 className="text-2xl font-bold mb-4">Key Factors to Consider</h3>
            <div className="space-y-6 mb-8">
              <div className="border-l-4 border-green-500 pl-6">
                <h4 className="font-bold text-lg mb-2">1. Payout Rates</h4>
                <p className="text-gray-700">Look for platforms offering 80-93% of card value. Rates vary by brand and demand.</p>
              </div>
              
              <div className="border-l-4 border-blue-500 pl-6">
                <h4 className="font-bold text-lg mb-2">2. Payment Speed</h4>
                <p className="text-gray-700">Best platforms offer same-day or next-day payment processing.</p>
              </div>
              
              <div className="border-l-4 border-purple-500 pl-6">
                <h4 className="font-bold text-lg mb-2">3. Security Measures</h4>
                <p className="text-gray-700">SSL encryption, secure payment methods, and fraud protection are essential.</p>
              </div>
              
              <div className="border-l-4 border-pink-500 pl-6">
                <h4 className="font-bold text-lg mb-2">4. Accepted Brands</h4>
                <p className="text-gray-700">Platforms accepting 100+ brands give you more selling opportunities.</p>
              </div>
            </div>

            <h3 className="text-2xl font-bold mb-4">Why Choose Cashifygcmart?</h3>
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="bg-green-50 p-6 rounded-lg">
                <h4 className="font-bold text-lg mb-3 text-green-800">✓ Industry-Leading Rates</h4>
                <p className="text-gray-700">Up to 93% of gift card face value</p>
              </div>
              <div className="bg-blue-50 p-6 rounded-lg">
                <h4 className="font-bold text-lg mb-3 text-blue-800">✓ Same-Day Payments</h4>
                <p className="text-gray-700">Get cash within 24 hours</p>
              </div>
              <div className="bg-purple-50 p-6 rounded-lg">
                <h4 className="font-bold text-lg mb-3 text-purple-800">✓ 230+ Trusted Vendors</h4>
                <p className="text-gray-700">Largest network of verified buyers</p>
              </div>
              <div className="bg-pink-50 p-6 rounded-lg">
                <h4 className="font-bold text-lg mb-3 text-pink-800">✓ SSL Secured</h4>
                <p className="text-gray-700">Bank-level security protection</p>
              </div>
            </div>
          </section>

          {/* Section 4 */}
          <section id="safety-tips" className="mb-12">
            <h2 className="text-3xl font-bold mb-6 text-gray-800">Safety and Security Tips</h2>
            
            <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-lg mb-8">
              <div className="flex items-start space-x-3">
                <AlertTriangle className="text-red-500 mt-1 flex-shrink-0" size={24} />
                <div>
                  <h3 className="font-bold text-lg mb-2 text-red-800">⚠️ Warning Signs to Avoid</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Platforms asking for gift card codes before payment</li>
                    <li>• Unusually high rates (over 95%)</li>
                    <li>• No SSL certificate or security measures</li>
                    <li>• Poor customer reviews or no reviews</li>
                    <li>• Pressure to sell immediately</li>
                  </ul>
                </div>
              </div>
            </div>

            <h3 className="text-2xl font-bold mb-4">Best Practices for Safe Selling</h3>
            <div className="space-y-4">
              <div className="flex items-start space-x-3 bg-green-50 p-4 rounded-lg">
                <CheckCircle className="text-green-500 mt-1 flex-shrink-0" size={20} />
                <div>
                  <h4 className="font-semibold">Research the Platform</h4>
                  <p className="text-gray-600">Check reviews, security certificates, and business registration</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3 bg-green-50 p-4 rounded-lg">
                <CheckCircle className="text-green-500 mt-1 flex-shrink-0" size={20} />
                <div>
                  <h4 className="font-semibold">Verify Card Balance First</h4>
                  <p className="text-gray-600">Check your card balance before submitting to avoid disputes</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3 bg-green-50 p-4 rounded-lg">
                <CheckCircle className="text-green-500 mt-1 flex-shrink-0" size={20} />
                <div>
                  <h4 className="font-semibold">Keep Documentation</h4>
                  <p className="text-gray-600">Save receipts, screenshots, and transaction records</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3 bg-green-50 p-4 rounded-lg">
                <CheckCircle className="text-green-500 mt-1 flex-shrink-0" size={20} />
                <div>
                  <h4 className="font-semibold">Use Secure Payment Methods</h4>
                  <p className="text-gray-600">Prefer PayPal, bank transfers, or other traceable payment methods</p>
                </div>
              </div>
            </div>
          </section>

          {/* Section 5 */}
          <section id="maximizing-value" className="mb-12">
            <h2 className="text-3xl font-bold mb-6 text-gray-800">Maximizing Your Gift Card Value</h2>
            
            <h3 className="text-2xl font-bold mb-4">Timing Your Sale</h3>
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="bg-blue-50 p-6 rounded-lg">
                <h4 className="font-bold text-lg mb-3 text-blue-800">🎄 Holiday Season (Nov-Jan)</h4>
                <p className="text-gray-700">Higher demand = better rates for popular brands</p>
              </div>
              <div className="bg-green-50 p-6 rounded-lg">
                <h4 className="font-bold text-lg mb-3 text-green-800">🛍️ Back-to-School (Jul-Sep)</h4>
                <p className="text-gray-700">Increased rates for electronics and clothing brands</p>
              </div>
            </div>

            <h3 className="text-2xl font-bold mb-4">High-Value Gift Card Brands</h3>
            <div className="overflow-x-auto mb-6">
              <table className="w-full border-collapse border border-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="border border-gray-300 p-3 text-left">Brand</th>
                    <th className="border border-gray-300 p-3 text-left">Typical Rate</th>
                    <th className="border border-gray-300 p-3 text-left">Best Time to Sell</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-gray-300 p-3">Amazon</td>
                    <td className="border border-gray-300 p-3 text-green-600 font-semibold">85-93%</td>
                    <td className="border border-gray-300 p-3">Year-round high demand</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="border border-gray-300 p-3">Apple/iTunes</td>
                    <td className="border border-gray-300 p-3 text-green-600 font-semibold">80-90%</td>
                    <td className="border border-gray-300 p-3">Product launch seasons</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 p-3">Walmart</td>
                    <td className="border border-gray-300 p-3 text-green-600 font-semibold">82-88%</td>
                    <td className="border border-gray-300 p-3">Holiday seasons</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="border border-gray-300 p-3">Target</td>
                    <td className="border border-gray-300 p-3 text-green-600 font-semibold">80-87%</td>
                    <td className="border border-gray-300 p-3">Back-to-school, holidays</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Section 6 */}
          <section id="step-by-step" className="mb-12">
            <h2 className="text-3xl font-bold mb-6 text-gray-800">Step-by-Step Selling Process</h2>
            
            <div className="space-y-8">
              <div className="border-l-4 border-blue-500 pl-6 py-4">
                <h3 className="text-xl font-bold mb-3">Step 1: Check Your Card Balance</h3>
                <p className="text-gray-700 mb-3">Before listing your card, verify the exact balance by:</p>
                <ul className="list-disc list-inside text-gray-600 space-y-1">
                  <li>Calling the customer service number on the back</li>
                  <li>Checking the brand's official website</li>
                  <li>Visiting a physical store location</li>
                </ul>
              </div>

              <div className="border-l-4 border-green-500 pl-6 py-4">
                <h3 className="text-xl font-bold mb-3">Step 2: Get an Instant Quote</h3>
                <p className="text-gray-700 mb-3">Use our rate calculator to see current market rates:</p>
                <Link to="/rate-calculator" className="inline-block bg-green-500 text-white px-6 py-3 rounded-lg hover:bg-green-600 transition-colors">
                  Calculate Your Card Value →
                </Link>
              </div>

              <div className="border-l-4 border-purple-500 pl-6 py-4">
                <h3 className="text-xl font-bold mb-3">Step 3: Submit Your Card</h3>
                <p className="text-gray-700 mb-3">Complete our secure submission form:</p>
                <ul className="list-disc list-inside text-gray-600 space-y-1">
                  <li>Upload clear photos of physical cards (front and back)</li>
                  <li>Enter digital codes for e-gift cards</li>
                  <li>Provide your payment information</li>
                </ul>
              </div>

              <div className="border-l-4 border-pink-500 pl-6 py-4">
                <h3 className="text-xl font-bold mb-3">Step 4: Wait for Verification</h3>
                <p className="text-gray-700">Our team verifies card authenticity within 2-4 hours during business hours.</p>
              </div>

              <div className="border-l-4 border-orange-500 pl-6 py-4">
                <h3 className="text-xl font-bold mb-3">Step 5: Get Paid</h3>
                <p className="text-gray-700 mb-3">Once verified, receive payment via:</p>
                <ul className="list-disc list-inside text-gray-600 space-y-1">
                  <li>PayPal (instant)</li>
                  <li>Bank transfer (same day)</li>
                  <li>Check (3-5 business days)</li>
                </ul>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white p-8 rounded-lg text-center">
            <h2 className="text-3xl font-bold mb-4">Ready to Turn Your Gift Cards into Cash?</h2>
            <p className="text-xl mb-6">Get started with our simple, secure process today!</p>
            <div className="space-y-4 sm:space-y-0 sm:space-x-4 sm:flex sm:justify-center">
              <Link to="/rate-calculator" className="block sm:inline-block bg-white text-purple-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
                Get Instant Quote
              </Link>
              <Link to="/form-submission" className="block sm:inline-block bg-purple-700 text-white px-8 py-3 rounded-lg font-semibold hover:bg-purple-800 transition-colors border border-white">
                Start Selling Now
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GiftCardGuide;